import { motion } from 'framer-motion';
import { Clock, Users, MapPin, ArrowUpRight, TrendingUp } from 'lucide-react';
import { formatINR, formatCommission } from '@/utils/currency';
import { useNavigate } from 'react-router-dom';
import { getCurrentTier } from '@/config/brokerTiers';

function getBrokerFacingRate(tier, maxDistributable) {
  const brokerRate = maxDistributable * tier.rateMultiplier;
  return (brokerRate / 30) * 100;
}

export default function DealCard({ deal, featured = false, brokerCumulativeCapital = 500000 }) {
  const navigate = useNavigate();
  
  const currentTier = getCurrentTier(brokerCumulativeCapital);
  const brokerFacingRate = getBrokerFacingRate(currentTier, deal.maxDistributable || 2.5);
  
  const milestonePercent = 30;
  const milestoneAmount = deal.assetValue * (milestonePercent / 100);
  const maxCommission = milestoneAmount * (brokerFacingRate / 100);
  
  // DEFENSIVE: Guard against NaN with proper fallbacks
  const capitalRequired = deal.capitalRequired || deal.capitalTarget || deal.assetValue * 0.3 || 1;
  const capitalRaised = deal.capitalRaised || 0;
  const fundingPercent = capitalRequired > 0 
    ? Math.min(100, (capitalRaised / capitalRequired) * 100) 
    : (deal.fundingPercent || 100);
  const balanceRemaining = Math.max(0, capitalRequired - capitalRaised);
  const isClosing = deal.daysLeft <= 7;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.01 }}
      onClick={() => navigate(`/broker/deal/${deal.id}`)}
      className={`
        relative overflow-hidden cursor-pointer transition-all duration-300
        bg-[#0A0A0A] border rounded-sm
        ${featured 
          ? 'border-[#4ADE80]/30 shadow-[0_0_20px_rgba(74,222,128,0.1)]' 
          : 'border-white/10 hover:border-white/20'
        }
      `}
      data-testid={`deal-card-${deal.id}`}
    >
      {/* Closing Soon Badge */}
      {isClosing && (
        <div className="absolute top-0 right-0 px-3 py-1 bg-[#4ADE80] text-black text-xs font-mono uppercase">
          Closing Soon
        </div>
      )}

      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-light text-white mb-1">{deal.name}</h3>
            <div className="flex items-center gap-1 text-white/40 text-sm">
              <MapPin className="w-3 h-3" />
              {deal.location}
            </div>
          </div>
          <ArrowUpRight className="w-5 h-5 text-white/30" />
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Asset Value</p>
            <p className="text-xl font-mono text-white">{formatINR(deal.assetValue)}</p>
          </div>
          <div>
            <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Capital Target</p>
            <p className="text-xl font-mono text-white">{formatINR(deal.capitalRequired)}</p>
          </div>
        </div>

        {/* Funding Progress - Enhanced UI */}
        <div className="bg-white/5 border border-white/10 rounded-sm p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-white/50 uppercase tracking-wider">Funding Status</span>
            <span className="text-sm font-mono text-[#4ADE80] font-medium">
              {isNaN(fundingPercent) ? '100' : fundingPercent.toFixed(0)}% Funded
            </span>
          </div>
          
          {/* Progress Bar */}
          <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-3">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${isNaN(fundingPercent) ? 100 : fundingPercent}%` }}
              transition={{ duration: 1, ease: 'easeOut' }}
              className={`h-full ${fundingPercent >= 90 ? 'bg-[#4ADE80]' : 'bg-[#4ADE80]/70'}`}
            />
          </div>
          
          {/* Funded vs Remaining */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-white/40 mb-1">Amount Funded</p>
              <p className="text-lg font-mono text-[#4ADE80]">{formatINR(capitalRaised)}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-white/40 mb-1">Balance to Fund</p>
              <p className="text-lg font-mono text-white">{formatINR(balanceRemaining)}</p>
            </div>
          </div>
        </div>

        {/* Commission Highlight */}
        <div className="bg-[#4ADE80]/10 border border-[#4ADE80]/20 rounded-sm p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[#4ADE80]" />
              <span className="text-sm text-white/60">Your Rate ({currentTier.name})</span>
            </div>
            <span className="text-2xl font-mono text-[#4ADE80]">{brokerFacingRate.toFixed(2)}%</span>
          </div>
          <p className="text-xs text-white/40 mt-2">
            Earn up to <span className="font-mono text-[#4ADE80]">{formatCommission(maxCommission)}</span> on full raise
          </p>
        </div>

        {/* Footer Stats */}
        <div className="flex items-center justify-between pt-4 border-t border-white/5">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1 text-white/50 text-sm">
              <Users className="w-4 h-4" />
              <span className="font-mono">{deal.filledSlots}/{deal.totalSlots}</span>
              <span className="text-white/30">slots</span>
            </div>
            <div className="flex items-center gap-1 text-white/50 text-sm">
              <Clock className="w-4 h-4" />
              <span className="font-mono">{deal.daysLeft}</span>
              <span className="text-white/30">days left</span>
            </div>
          </div>
          <div className="text-xs font-mono text-white/40">
            Min: {formatINR(deal.minTicket)}
          </div>
        </div>
      </div>
    </motion.div>
  );
}